import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOutUser, useAuth } from '../../auth/firebaseAuth';
import {
  User,
  Menu,
  X,
  ChevronDown,
  LogOut,
  MessageCircle,
  Search,
  HomeIcon,
  Briefcase,
  Grid,
  Bookmark,
  Info,
  Lightbulb
} from 'lucide-react';
import appLogo from '../../media/logo/main_logo_only_transparent.png';
import { isAgentOrisLandlord, isFsbo, isRenter } from '../../types/userTypes';

// --- Reusable Nav Button ---
const NavButton = ({ onClick, icon: Icon, children, active = false }: any) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 
    ${
      active
        ? 'bg-white/20 text-white'
        : 'text-blue-100 hover:bg-white/10 hover:text-white'
    }`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </button>
);

function NavBar() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  const isUserRenter = isRenter(userProfile);
  const isUserAgentOrLandlord = isAgentOrisLandlord(userProfile);
  const isUserFsbo = isFsbo(userProfile);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(e.target as Node)
      ) {
        setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => setIsOpen(false), [location.pathname]);

  const handleSignOut = async () => {
    await signOutUser();
    navigate('/');
    setIsAccountMenuOpen(false);
  };

  return (
    <nav className="sticky top-0 w-full z-50 bg-blue-600 shadow-lg border-b border-blue-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo Section */}
          <Link to="/" className="flex items-center group shrink-0">
            <img
              src={appLogo}
              alt="RentSmart Logo"
              className="w-10 h-10 object-contain transition-transform group-hover:scale-105"
            />
            <span className="ml-2 text-xl font-bold text-white tracking-tight no-underline">
              RentSmart.ie
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 mx-4">
            <NavButton
              onClick={() => navigate('/browse')}
              icon={Search}
              active={location.pathname === '/browse'}
            >
              Find Home
            </NavButton>
            <NavButton
              onClick={() => navigate('/list-your-property')}
              icon={HomeIcon}
              active={location.pathname === '/list-your-property'}
            >
              List Free
            </NavButton>

            {/* Property Tools Link Re-added */}
            <NavButton
              onClick={() => navigate('/property-tools')}
              icon={Lightbulb}
              active={location.pathname === '/property-tools'}
            >
              Property Tools
            </NavButton>

            {isUserFsbo && (
              <NavButton
                onClick={() => navigate('/dashboard/fsbo')}
                icon={HomeIcon}
                active={location.pathname === '/dashboard/fsbo'}
              >
                Seller Dashboard
              </NavButton>
            )}

            {(isUserRenter || !currentUser) && (
              <NavButton
                onClick={() => navigate('/tenant-toolkit')}
                icon={Briefcase}
                active={location.pathname === '/tenant-toolkit'}
              >
                Tenant Kit
              </NavButton>
            )}
            {(isUserAgentOrLandlord || !currentUser) && (
              <NavButton
                onClick={() => navigate('/landlord-toolkit')}
                icon={Grid}
                active={location.pathname === '/landlord-toolkit'}
              >
                Landlord Kit
              </NavButton>
            )}
          </div>

          {/* Right Side (Auth/Messages) */}
          <div className="hidden sm:flex items-center gap-2">
            {currentUser && (
              <button
                onClick={() => navigate('/messages')}
                className="p-2 text-blue-100 hover:text-white hover:bg-white/10 rounded-full relative mr-1 transition-colors"
                aria-label="Messages"
              >
                <MessageCircle className="h-5 w-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-orange-400 rounded-full border-2 border-blue-600"></span>
              </button>
            )}

            {currentUser ? (
              <div className="relative" ref={accountMenuRef}>
                <button
                  onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                  className="flex items-center gap-2 py-1.5 pl-1.5 pr-3 rounded-full bg-blue-700 hover:bg-blue-800 border border-blue-500 transition-all"
                >
                  <div className="h-7 w-7 rounded-full bg-white flex items-center justify-center text-blue-600 text-xs font-bold shadow-sm">
                    {currentUser.displayName?.charAt(0) || (
                      <User className="h-4 w-4" />
                    )}
                  </div>
                  <span className="text-sm text-white font-medium max-w-[90px] truncate">
                    {currentUser.displayName || 'Account'}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-blue-200 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isAccountMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 animate-in fade-in zoom-in-95 duration-100">
                    <AccountMenuItem
                      to="/profile"
                      icon={User}
                      label="My Profile"
                    />
                    {isUserAgentOrLandlord && (
                      <AccountMenuItem
                        to="/landlord-dashboard"
                        icon={Grid}
                        label="Landlord Dashboard"
                      />
                    )}

                    {isUserRenter && (
                      <AccountMenuItem
                        to="/bookmarks"
                        icon={Bookmark}
                        label="Saved Homes"
                      />
                    )}
                    <AccountMenuItem
                      to="/about-us"
                      icon={Info}
                      label="About Us"
                    />
                    <div className="h-px bg-gray-100 my-1 mx-2" />
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4 mr-3" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 text-sm font-medium text-white hover:bg-white/10 rounded-lg"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="px-5 py-2 text-sm font-bold text-blue-600 bg-white hover:bg-blue-50 rounded-lg shadow-md transition-transform active:scale-95"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="sm:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-white outline-none"
            >
              {isOpen ? (
                <X className="h-7 w-7" />
              ) : (
                <Menu className="h-7 w-7" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`sm:hidden bg-blue-700 overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[100vh] border-t border-blue-500' : 'max-h-0'}`}
      >
        <div className="px-4 py-6 space-y-2">
          <MobileLink
            onClick={() => navigate('/browse')}
            icon={Search}
            label="Find a Home"
          />
          <MobileLink
            onClick={() => navigate('/list-your-property')}
            icon={HomeIcon}
            label="List Property"
          />
          <MobileLink
            onClick={() => navigate('/property-tools')}
            icon={Lightbulb}
            label="Property Tools"
          />
          <MobileLink
            onClick={() => navigate('/tenant-toolkit')}
            icon={Briefcase}
            label="Tenant Toolkit"
          />
          <MobileLink
            onClick={() => navigate('/landlord-toolkit')}
            icon={Grid}
            label="Landlord Toolkit"
          />

          {currentUser && (
            <MobileLink
              onClick={() => navigate('/messages')}
              icon={MessageCircle}
              label="Messages"
            />
          )}

          <div className="pt-4 mt-4 border-t border-blue-600">
            {currentUser ? (
              <>
                <MobileLink
                  onClick={() => navigate('/profile')}
                  icon={User}
                  label="My Profile"
                />
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-4 p-4 text-red-200 hover:bg-blue-800 rounded-xl transition-colors"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium text-lg">Sign Out</span>
                </button>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-3 p-2">
                <button
                  onClick={() => navigate('/login')}
                  className="py-3 text-center rounded-xl bg-blue-800 text-white font-medium"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="py-3 text-center rounded-xl bg-white text-blue-600 font-bold"
                >
                  Join Free
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

// --- Sub-components ---
const AccountMenuItem = ({ to, icon: Icon, label }: any) => (
  <Link
    to={to}
    className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
  >
    <Icon className="h-4 w-4 mr-3 text-gray-400" />
    {label}
  </Link>
);

const MobileLink = ({ onClick, icon: Icon, label }: any) => (
  <button
    onClick={onClick}
    className="flex items-center gap-4 w-full p-4 rounded-xl hover:bg-blue-800 text-white transition-colors"
  >
    <Icon className="h-6 w-6 text-blue-200" />
    <span className="font-medium text-lg">{label}</span>
  </button>
);

export default NavBar;
